import React, { forwardRef, useMemo, useState } from 'react';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { DataGrid } from '../DataGrid/DataGrid';
import {
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { DateCell } from '../DataGrid/Cells/DateCell';
import { BmtCell } from '../DataGrid/Cells/BmtCell';
import { StatusCell } from '../DataGrid/Cells/StatusCell';
import { dateSortValue, formatDateSortValue } from '../../utils/dateUtils';
import { dateSortComparator, withUniqueGridRowIds } from '../../utils/gridUtils';
import { NumberCell } from '../DataGrid/Cells/NumberCell';

interface DataGridProps {
  data: any[];
  loading?: boolean;
  onRowClick?: (row: any) => void;
  hideMPAStatus: boolean;
}

const LOCAL_STORAGE_KEY = 'bunkeringLogsColumnVisibility';

const bunkeringLogsColumns: GridColDef[] = [
  {
    field: 'bunkerMeterTicketNumber',
    flex: 1,
    headerName: 'BMT',
    minWidth: 108,
    renderCell: (data: any) => {
      return <BmtCell value={data.value} />;
    },
  },
  {
    field: 'bargeInfo',
    headerName: 'Barge Name',
    flex: 1,
    minWidth: 120,
    valueGetter: (params: any) => {
      return params.row.bargeInfo.name;
    },
  },
  {
    field: 'mfmDeviceName',
    headerName: 'MFM Device',
    flex: 1,
    minWidth: 170,
    valueGetter: (params: GridRenderCellParams) => params.row?.deviceInfo?.name,
  },
  {
    field: 'massTotal',
    headerName: 'Mass Total',
    flex: 1,
    minWidth: 152,
    valueGetter: (params: any) => params.row?.massInventory?.totalMass,
    renderCell: (params: any) => {
      return (
        <NumberCell
          value={params.row.massInventory.totalMass}
          decimals={3}
          displayAllDecimals={true}
        />
      );
    },
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    flex: 1,
    minWidth: 148,
    valueGetter: (params) => dateSortValue(params.row.bunkerActivePeriod?.start?.date),
    valueFormatter: ({ value }) => formatDateSortValue(value as number | null),
    sortComparator: dateSortComparator,
    renderCell: (params: GridRenderCellParams) => {
      return <DateCell dateValue={params.row.bunkerActivePeriod?.start?.date} />;
    },
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    flex: 1,
    minWidth: 148,
    valueGetter: (params) => dateSortValue(params.row.bunkerActivePeriod?.end?.date),
    valueFormatter: ({ value }) => formatDateSortValue(value as number | null),
    sortComparator: dateSortComparator,
    renderCell: (params: GridRenderCellParams) => {
      return <DateCell dateValue={params.row.bunkerActivePeriod?.end?.date} />;
    },
  },
  {
    field: 'mpaTransmissionStatus',
    headerName: 'MPA Status',
    flex: 1,
    valueGetter: (params: GridRenderCellParams) => {
      return params.row?.mpaTransmissionStatus;
    },
    renderCell: (data: GridRenderCellParams) => {
      return <StatusCell statusCode={data.row.mpaTransmissionStatus} />;
    },
  },
  {
    field: 'beginForward',
    headerName: 'Begin Forward',
    flex: 1,
    minWidth: 152,
    valueGetter: (params: any) => params.row?.massInventory?.begin?.forward,
    renderCell: (params: any) => {
      return (
        <NumberCell
          value={params.row.massInventory.begin.forward}
          decimals={4}
          displayAllDecimals={true}
        />
      );
    },
  },
  {
    field: 'endForward',
    headerName: 'End Forward',
    flex: 1,
    minWidth: 152,
    valueGetter: (params: any) => params.row?.massInventory?.end?.forward,
    renderCell: (params: any) => {
      return (
        <NumberCell
          value={params.row.massInventory.end.forward}
          decimals={4}
          displayAllDecimals={true}
        />
      );
    },
  },
  {
    field: 'beginReverse',
    headerName: 'Begin Reverse',
    flex: 1,
    minWidth: 152,
    valueGetter: (params: any) => params.row?.massInventory?.begin?.reverse,
    renderCell: (params: any) => {
      return (
        <NumberCell
          value={params.row.massInventory.begin.reverse}
          decimals={4}
          displayAllDecimals={true}
        />
      );
    },
  },
  {
    field: 'endReverse',
    headerName: 'End Reverse',
    flex: 1,
    minWidth: 152,
    valueGetter: (params: any) => params.row?.massInventory?.end?.reverse,
    renderCell: (params: any) => {
      return (
        <NumberCell
          value={params.row.massInventory.end.reverse}
          decimals={4}
          displayAllDecimals={true}
        />
      );
    },
  },
  {
    field: 'averageDensity',
    headerName: 'Average Density',
    flex: 1,
    minWidth: 168,
    renderCell: (params: any) => {
      return (
        <NumberCell value={params.row.averageDensity} decimals={4} displayAllDecimals={true} />
      );
    },
  },
  {
    field: 'aerationLimit',
    headerName: 'Aeration Limit',
    flex: 1,
    minWidth: 160,
    valueGetter: (params: any) => {
      return params.row?.aerationLimit || 0;
    },
    renderCell: (params: any) => {
      return <NumberCell value={params.row.aerationLimit} decimals={4} displayAllDecimals={true} />;
    },
  },
];

export const BunkeringLogsDataGrid = forwardRef<GridApiPremium | null, DataGridProps>(
  ({ data, loading, onRowClick, hideMPAStatus }, ref) => {
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
      () => {
        const savedModel = localStorage.getItem(LOCAL_STORAGE_KEY);
        return savedModel ? JSON.parse(savedModel) : {};
      },
    );

    const handleColumnVisibilityChange = (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newModel));
    };

    const effectiveColumnVisibilityModel = useMemo(
      () => ({
        ...columnVisibilityModel,
        ...(hideMPAStatus ? { mpaTransmissionStatus: false } : {}),
      }),
      [columnVisibilityModel, hideMPAStatus],
    );

    const rows = useMemo(
      () => withUniqueGridRowIds(data, (row) => row.id as string | number | undefined),
      [data],
    );

    return (
      <DataGrid
        columnVisibilityModel={effectiveColumnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        sx={{
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
        rows={rows}
        ref={ref}
        loading={loading}
        columns={bunkeringLogsColumns}
        onRowClick={onRowClick}
        getRowId={(row) => row.__gridRowId}
      />
    );
  },
);
